import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import usageService from '../services/usageService';
import {
  UsageStats,
  PlanName,
  PLAN_LIMITS,
  isTemplateAvailable as checkTemplateAvailable,
} from '../types/usage.types';

interface UsageContextType {
  usageStats: UsageStats | null;
  loading: boolean;
  error: string | null;
  refreshUsage: () => Promise<void>;

  // Limit check helpers - return true if operation is allowed
  canCreateProject: () => boolean;
  canSendMessage: () => boolean;
  canCreateArtifact: () => boolean;
  canExportFigma: () => boolean;
  isTemplateAllowed: (templateTitle: string) => boolean;

  // Remaining counts
  getProjectsRemaining: () => number;
  getMessagesRemaining: () => number;
  getArtifactsRemaining: () => number | 'unlimited';
  getFigmaExportsRemaining: () => number | 'unlimited';

  // Error messages for limit alerts
  getProjectLimitMessage: () => string;
  getMessageLimitMessage: () => string;
  getArtifactLimitMessage: () => string;
  getFigmaExportLimitMessage: () => string;
  getTemplateLimitMessage: () => string;

  // Track Figma export
  trackFigmaExport: (artifactId?: string, projectId?: string) => Promise<boolean>;
}

const UsageContext = createContext<UsageContextType | undefined>(undefined);

export const useUsage = () => {
  const context = useContext(UsageContext);
  if (context === undefined) {
    throw new Error('useUsage must be used within a UsageProvider');
  }
  return context;
};

export const UsageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshUsage = useCallback(async () => {
    if (!currentUser) {
      setUsageStats(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const stats = await usageService.getUsageStatus();
      setUsageStats(stats);
    } catch (err: any) {
      console.error('Error fetching usage:', err);
      setError(err.message || 'Failed to fetch usage status');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch usage when user changes OR when their subscription plan changes
  // (e.g. after an upgrade — without re-running on plan changes the cached
  // starter limits would persist until a manual browser refresh).
  useEffect(() => {
    if (currentUser) {
      refreshUsage();
    } else {
      setUsageStats(null);
    }
  }, [currentUser, userProfile?.subscription?.planName, refreshUsage]);

  // Helper functions
  const getPlanName = (): PlanName => usageStats?.plan || 'starter';

  const canCreateProject = (): boolean => {
    if (!usageStats) return false;
    const { current, limit } = usageStats.usage.projects;
    return current < limit;
  };

  const canSendMessage = (): boolean => {
    if (!usageStats) return false;
    const { current, limit } = usageStats.usage.messages;
    return current < limit;
  };

  const canCreateArtifact = (): boolean => {
    if (!usageStats) return false;
    const { current, limit } = usageStats.usage.artifacts;
    if (limit === null) return true; // Unlimited
    return current < limit;
  };

  const canExportFigma = (): boolean => {
    if (!usageStats) return false;
    const { current, limit } = usageStats.usage.figmaExports;
    if (limit === null) return true; // Unlimited
    return current < limit;
  };

  const isTemplateAllowed = (templateTitle: string): boolean => {
    return checkTemplateAvailable(templateTitle, getPlanName());
  };

  // Remaining counts
  const getProjectsRemaining = (): number => {
    if (!usageStats) return 0;
    const { current, limit } = usageStats.usage.projects;
    return Math.max(0, limit - current);
  };

  const getMessagesRemaining = (): number => {
    if (!usageStats) return 0;
    const { current, limit } = usageStats.usage.messages;
    return Math.max(0, limit - current);
  };

  const getArtifactsRemaining = (): number | 'unlimited' => {
    if (!usageStats) return 0;
    const { current, limit } = usageStats.usage.artifacts;
    if (limit === null) return 'unlimited';
    return Math.max(0, limit - current);
  };

  const getFigmaExportsRemaining = (): number | 'unlimited' => {
    if (!usageStats) return 0;
    const { current, limit } = usageStats.usage.figmaExports;
    if (limit === null) return 'unlimited';
    return Math.max(0, limit - current);
  };

  // Error messages
  const getProjectLimitMessage = (): string => {
    const plan = getPlanName();
    const limit = PLAN_LIMITS[plan].projects;
    if (plan === 'starter') {
      return `You've reached your project limit of ${limit}. Upgrade to Pro for up to 10 projects.`;
    }
    if (plan === 'pro') {
      return `You've reached your project limit of ${limit}. Upgrade to Ultra for up to 25 projects.`;
    }
    return `You've reached your project limit of ${limit}.`;
  };

  const getMessageLimitMessage = (): string => {
    const plan = getPlanName();
    const limit = PLAN_LIMITS[plan].messages;
    if (plan === 'starter') {
      return `You've used all ${limit} messages. Upgrade to Pro for 500 messages per month.`;
    }
    if (plan === 'pro') {
      return `You've used all your messages this month. Upgrade to Ultra for 1,000 messages per month.`;
    }
    return `You've reached your message limit for this billing period.`;
  };

  const getArtifactLimitMessage = (): string => {
    const plan = getPlanName();
    const limit = PLAN_LIMITS[plan].artifacts;
    if (plan === 'starter') {
      return `You've reached your artifact limit of ${limit}. Upgrade to Pro for 25 artifacts per month.`;
    }
    if (plan === 'pro') {
      return `You've used all your artifacts this month. Upgrade to Ultra for 50 artifacts per month.`;
    }
    return `You've reached your artifact limit for this billing period.`;
  };

  const getFigmaExportLimitMessage = (): string => {
    const plan = getPlanName();
    const limit = PLAN_LIMITS[plan].figmaExports;
    if (plan === 'starter') {
      return `You've used your ${limit} Figma export. Upgrade to Pro for 25 exports per month.`;
    }
    if (plan === 'pro') {
      return `You've used all your Figma exports this month. Upgrade to Ultra for unlimited exports.`;
    }
    return `You've reached your Figma export limit.`;
  };

  const getTemplateLimitMessage = (): string => {
    return 'This template is only available on Pro and Ultra plans. Upgrade to unlock all templates.';
  };

  // Track Figma export
  const trackFigmaExport = async (artifactId?: string, projectId?: string): Promise<boolean> => {
    try {
      await usageService.trackFigmaExport(artifactId, projectId);
      await refreshUsage(); // Refresh usage after tracking
      return true;
    } catch (err: any) {
      console.error('Error tracking Figma export:', err);
      return false;
    }
  };

  const value: UsageContextType = {
    usageStats,
    loading,
    error,
    refreshUsage,
    canCreateProject,
    canSendMessage,
    canCreateArtifact,
    canExportFigma,
    isTemplateAllowed,
    getProjectsRemaining,
    getMessagesRemaining,
    getArtifactsRemaining,
    getFigmaExportsRemaining,
    getProjectLimitMessage,
    getMessageLimitMessage,
    getArtifactLimitMessage,
    getFigmaExportLimitMessage,
    getTemplateLimitMessage,
    trackFigmaExport,
  };

  return <UsageContext.Provider value={value}>{children}</UsageContext.Provider>;
};
